import { env } from "next-runtime-env";
import { z } from "zod";

const clientEnvSchema = z.object({
  NEXT_PUBLIC_API_URL: z.string().min(1),
  NEXT_PUBLIC_COGNITO_USER_POOL_ID: z.string().min(1),
  NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID: z.string().min(1),
  NEXT_PUBLIC_COGNITO_DOMAIN: z.string().min(1),
  NEXT_PUBLIC_WEB_ORIGIN: z.string().min(1),
  NEXT_PUBLIC_PROCORE_API_URL: z.string().min(1),
  NEXT_PUBLIC_PROCORE_APP_URL: z.string().min(1),
  NEXT_PUBLIC_PROCORE_PLUGIN_URL: z.string().min(1),
  NEXT_PUBLIC_UPPY_ONEDRIVE_CLIENT_KEY: z.string().min(1),
  NEXT_PUBLIC_UPPY_ONEDRIVE_CREDENTIALS_NAME: z.string().min(1),
  NEXT_PUBLIC_PYLON_APP_ID: z.string().optional(),
  NEXT_PUBLIC_FULLSTORY_ORG: z.string().optional(),
  NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID: z.string().optional(),
  NEXT_PUBLIC_LIVEBLOCKS_API_KEY: z.string().optional(),
  NEXT_PUBLIC_NUTRIENT_LICENSE_KEY: z.string().optional(),
  NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: z.string().optional(),
  NEXT_PUBLIC_ROLLBAR_ENV: z.string().optional(),
  NEXT_PUBLIC_COMMIT_SHA: z.string().optional(),
  NEXT_PUBLIC_GRAPH_API_URL: z.string().optional(),
  NEXT_PUBLIC_CHAMELEON_ENABLED: z.string().optional(),
  NEXT_PUBLIC_DOCINTEL_URL: z.string().optional(),
  NEXT_PUBLIC_E2E: z.string().optional(),
  NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string().optional(),
  NEXT_PUBLIC_DX_TRIAL_SKU_ID: z.string().min(1),
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;

function readClientEnv(): ClientEnv {
  return clientEnvSchema.parse({
    NEXT_PUBLIC_API_URL: env("NEXT_PUBLIC_API_URL") || "https://dc-two-api.ml.documentcrunch.com",
    NEXT_PUBLIC_COGNITO_USER_POOL_ID: env("NEXT_PUBLIC_COGNITO_USER_POOL_ID") || "us-east-1_OYmMb4FkG",
    NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID:
      env("NEXT_PUBLIC_COGNITO_USER_POOL_CLIENT_ID") || "6308f14jfnhdsspjtrjtmooeqc",
    NEXT_PUBLIC_COGNITO_DOMAIN: env("NEXT_PUBLIC_COGNITO_DOMAIN") || "auth.ml.documentcrunch.com",
    NEXT_PUBLIC_WEB_ORIGIN: env("NEXT_PUBLIC_WEB_ORIGIN") || "http://localhost:3001",
    NEXT_PUBLIC_PROCORE_API_URL: env("NEXT_PUBLIC_PROCORE_API_URL") || "https://sandbox.procore.com/rest/",
    NEXT_PUBLIC_PROCORE_APP_URL: env("NEXT_PUBLIC_PROCORE_APP_URL") || "https://sandbox.procore.com/",
    NEXT_PUBLIC_PROCORE_PLUGIN_URL: env("NEXT_PUBLIC_PROCORE_PLUGIN_URL") || "https://procore.ml.documentcrunch.com/",
    NEXT_PUBLIC_UPPY_ONEDRIVE_CLIENT_KEY:
      env("NEXT_PUBLIC_UPPY_ONEDRIVE_CLIENT_KEY") || "ba4fb56f1745d053e528fd2d7a635b27",
    NEXT_PUBLIC_UPPY_ONEDRIVE_CREDENTIALS_NAME:
      env("NEXT_PUBLIC_UPPY_ONEDRIVE_CREDENTIALS_NAME") || "dc-onedrive-integration",
    NEXT_PUBLIC_PYLON_APP_ID: env("NEXT_PUBLIC_PYLON_APP_ID") || undefined,
    NEXT_PUBLIC_FULLSTORY_ORG: env("NEXT_PUBLIC_FULLSTORY_ORG") || undefined,
    // Do NOT add a hardcoded fallback here — LD's streaming SSE connection breaks E2E networkidle checks
    NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID: env("NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID") || undefined,
    NEXT_PUBLIC_LIVEBLOCKS_API_KEY: env("NEXT_PUBLIC_LIVEBLOCKS_API_KEY") || undefined,
    NEXT_PUBLIC_NUTRIENT_LICENSE_KEY: env("NEXT_PUBLIC_NUTRIENT_LICENSE_KEY") || undefined,
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: env("NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN") || undefined,
    NEXT_PUBLIC_ROLLBAR_ENV: env("NEXT_PUBLIC_ROLLBAR_ENV") || undefined,
    NEXT_PUBLIC_COMMIT_SHA: env("NEXT_PUBLIC_COMMIT_SHA") || undefined,
    NEXT_PUBLIC_GRAPH_API_URL: env("NEXT_PUBLIC_GRAPH_API_URL") || undefined,
    NEXT_PUBLIC_CHAMELEON_ENABLED: env("NEXT_PUBLIC_CHAMELEON_ENABLED") || undefined,
    NEXT_PUBLIC_DOCINTEL_URL: env("NEXT_PUBLIC_DOCINTEL_URL") || undefined,
    NEXT_PUBLIC_E2E: env("NEXT_PUBLIC_E2E") || undefined,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: env("NEXT_PUBLIC_TURNSTILE_SITE_KEY") || undefined,
    NEXT_PUBLIC_DX_TRIAL_SKU_ID: env("NEXT_PUBLIC_DX_TRIAL_SKU_ID") || "BS-SB-DC-ENT",
  });
}

let _clientEnv: ClientEnv | null = null;

export function clientEnv(): ClientEnv {
  if (!_clientEnv) _clientEnv = readClientEnv();
  return _clientEnv;
}

/**
 * True when running against an E2E deployment (docker sandbox, CI sandbox, or a feature
 * branch with NEXT_PUBLIC_E2E=true). Gates test-only auth behavior — persisting the
 * Trimble session to localStorage and rehydrating it on cold load so authenticated UI
 * tests render without a per-navigation SSO redirect. Never set in staging/prod.
 */
export function isE2E(): boolean {
  return env("NEXT_PUBLIC_E2E") === "true";
}
